/**
 * Utility functions for calendar date field handling
 */

import type { CalendarEvent, ElementColors, FormField, FormSettingsWithCalendar } from '../@types/calendar';
import type { FieldFilters } from '../@types/common';
import type { ViewColumn } from '../@types/views';
import type { DynamicPageFilters } from '../redux/slices/dynamic-page-slice';
import { convertToFieldFilters } from '../components/dynamic-page/menu/data-grid-menu-utils';
import {
  processRecordFilterForContinuousScroller,
  processLastEditedFilterForContinousScroller,
} from './dynamic-page-utils';
import { getTextColor } from './dom-utils';

export const getFieldValueById = (record: unknown, fieldId: number | null): string | undefined => {
  if (!fieldId || !record) return undefined;

  const fieldKey = fieldId.toString();
  const recordObj = record as Record<string, unknown>;
  const value = recordObj[fieldKey];

  if (value) {
    return value as string;
  }

  return undefined;
};

export const extractDateFieldInfo = (formSettings: unknown) => {
  if (!formSettings) {
    return { startFieldId: null, endFieldId: null };
  }

  const settings = formSettings as FormSettingsWithCalendar;

  if (!settings.fieldGroups) {
    return { startFieldId: null, endFieldId: null };
  }

  // Collect all fields from all groups
  const allFields: FormField[] = [];
  settings.fieldGroups.forEach((group) => {
    if (group.fields) {
      allFields.push(...group.fields);
    }
  });

  // Find the start date field by looking for dateField === true
  const startField = allFields.find((field: FormField) => field.dateField === true);

  // Find the end date field by looking for dateEndField === true
  const endField = allFields.find((field: FormField) => field.dateEndField === true);

  if (!startField) {
    return { startFieldId: null, endFieldId: null };
  }

  const startFieldId = startField.id;
  const endFieldId = endField ? endField.id : null;
  const startFieldName = startField?.fieldCaption?.translations?.[0]?.value || 'Start Date';
  const endFieldName = endField?.fieldCaption?.translations?.[0]?.value || 'End Date';

  const result = {
    startFieldId: startFieldId,
    endFieldId: endFieldId,
    startFieldName,
    endFieldName,
  };

  return result;
};

/**
 * Build calendar filters from view configuration and selected filters
 * Processes various filter types (MULTISELECTRANGE, STRING, NUMBER, DATE, BOOLEAN)
 * and converts them to FieldFilters format for API consumption
 *
 * @param selectedFilters - Dynamic page filters from Redux state
 * @param viewColumnsData - View column configurations from API
 * @returns FieldFilters ready for API consumption
 */

export const buildCalendarFilters = (
  selectedFilters?: DynamicPageFilters,
  viewColumnsData?: ViewColumn[]
): FieldFilters => {
  const continuousScrollerFilters = [];

  if (selectedFilters?.records) {
    const recordFilter = processRecordFilterForContinuousScroller(selectedFilters.records);
    if (recordFilter && recordFilter.value !== undefined && recordFilter.value !== null) {
      continuousScrollerFilters.push(recordFilter);
    }
  }

  if (selectedFilters?.lastEdited) {
    const lastEditedFilter = processLastEditedFilterForContinousScroller(selectedFilters.lastEdited);
    if (lastEditedFilter && lastEditedFilter.value !== undefined && lastEditedFilter.value !== null) {
      continuousScrollerFilters.push(lastEditedFilter);
    }
  }
  // Add view specific filters
  if (viewColumnsData?.length) {
    viewColumnsData.forEach((column: ViewColumn) => {
      if (!column.entityReference || !column.filterConfiguration) {
        return;
      }

      const filterType = column.entityReference.filterType;
      const entityType = column.entityReference.entityType;
      const entityPropertyName = column.entityReference.entityPropertyName;
      const entityId = column.entityReference.entityId;

      let fieldKey = '';
      if (entityType === 'KEYWORD' && entityPropertyName) {
        fieldKey = entityPropertyName;
      } else if (entityId) {
        fieldKey = entityId.toString();
      }

      if (!fieldKey) {
        return;
      }

      switch (filterType) {
        case 'MULTISELECTRANGE': {
          if (column.filterConfiguration.values?.length) {
            const values: (string | number)[] = [];

            if (column.filterConfiguration.includeBlank) {
              values.push('blank');
            }

            column.filterConfiguration.values.forEach(
              (item: { id?: string | number; optionIds?: string | number; value?: string | number }) => {
                const itemValue = item.id ?? item.optionIds ?? item.value;
                if (itemValue !== undefined && itemValue !== null) {
                  values.push(itemValue);
                }
              }
            );

            if (values.length > 0) {
              continuousScrollerFilters.push({
                scrollerId: column.id || 0,
                field: fieldKey,
                comparison: 0,
                value: values,
                customDataProperties: {
                  entityType,
                  entityPropertyName,
                  entityId,
                },
              });
            }
          }
          break;
        }

        case 'STRING': {
          if (column.filterConfiguration.targetValue && typeof column.filterConfiguration.targetValue === 'string') {
            const textValue = column.filterConfiguration.targetValue.trim();
            if (textValue) {
              continuousScrollerFilters.push({
                scrollerId: column.id || 0,
                field: fieldKey,
                comparison: 0,
                value: textValue,
                customDataProperties: {
                  entityType,
                  entityPropertyName,
                  entityId,
                },
              });
            }
          }
          break;
        }

        case 'NUMBER':
        case 'NUMBER_EQUALS': {
          if (
            column.filterConfiguration.leftBoundary !== undefined ||
            column.filterConfiguration.rightBoundary !== undefined
          ) {
            continuousScrollerFilters.push({
              scrollerId: column.id || 0,
              field: fieldKey,
              comparison: 3,
              value: {
                min: column.filterConfiguration.leftBoundary,
                max: column.filterConfiguration.rightBoundary,
              },
              customDataProperties: {
                entityType,
                entityPropertyName,
                entityId,
              },
            });
          } else if (
            column.filterConfiguration.targetValue !== undefined &&
            column.filterConfiguration.targetValue !== null
          ) {
            let numberValue: number;
            if (typeof column.filterConfiguration.targetValue === 'string') {
              numberValue = parseFloat(column.filterConfiguration.targetValue);
            } else if (typeof column.filterConfiguration.targetValue === 'number') {
              numberValue = column.filterConfiguration.targetValue;
            } else {
              numberValue = Number(column.filterConfiguration.targetValue);
            }

            if (!isNaN(numberValue)) {
              const numberFilter = {
                scrollerId: column.id || 0,
                field: fieldKey,
                comparison: 1,
                value: numberValue,
                customDataProperties: {
                  entityType,
                  entityPropertyName,
                  entityId,
                },
              };
              continuousScrollerFilters.push(numberFilter);
            }
          }
          break;
        }

        case 'DATE': {
          if (column.filterConfiguration.leftBoundary || column.filterConfiguration.rightBoundary) {
            if (
              column.filterConfiguration.leftBoundary !== undefined &&
              column.filterConfiguration.leftBoundary !== null
            ) {
              continuousScrollerFilters.push({
                scrollerId: `${column.id || 0}_gte`,
                field: fieldKey,
                comparison: 6,
                value: column.filterConfiguration.leftBoundary,
                customDataProperties: {
                  entityType,
                  entityPropertyName,
                  entityId,
                },
              });
            }

            if (
              column.filterConfiguration.rightBoundary !== undefined &&
              column.filterConfiguration.rightBoundary !== null
            ) {
              continuousScrollerFilters.push({
                scrollerId: `${column.id || 0}_lte`,
                field: fieldKey,
                comparison: 4,
                value: column.filterConfiguration.rightBoundary,
                customDataProperties: {
                  entityType,
                  entityPropertyName,
                  entityId,
                },
              });
            }
          }
          break;
        }

        case 'BOOLEAN': {
          if (
            column.filterConfiguration.targetValue !== undefined &&
            typeof column.filterConfiguration.targetValue === 'boolean'
          ) {
            continuousScrollerFilters.push({
              scrollerId: column.id || 0,
              field: fieldKey,
              comparison: 1,
              value: column.filterConfiguration.targetValue,
              customDataProperties: {
                entityType,
                entityPropertyName,
                entityId,
              },
            });
          }
          break;
        }

        default:
          // Skip unsupported filter types
          break;
      }
    });
  }

  const fieldFilters = convertToFieldFilters(continuousScrollerFilters);
  return fieldFilters;
};

/**
 * Darken a color by a specified factor
 */
export const darkenColor = (color: string, factor: number): string => {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.floor(parseInt(hex.substring(0, 2), 16) * (1 - factor)));
  const g = Math.max(0, Math.floor(parseInt(hex.substring(2, 4), 16) * (1 - factor)));
  const b = Math.max(0, Math.floor(parseInt(hex.substring(4, 6), 16) * (1 - factor)));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Get workflow step colors for calendar events using project theme
 */
export const getWorkflowStepColors = (
  status: string,
  workflowSteps?: Array<{ label: string; backgroundColor?: string }>
): ElementColors => {
  if (!status || !workflowSteps) {
    // Use theme primary colors as fallback
    return {
      backgroundColor: 'var(--primary-2)',
      borderColor: 'var(--primary-1)',
      textColor: 'var(--mono-1)',
    };
  }

  const step = workflowSteps.find((step) => step.label === status);
  const backgroundColor = step?.backgroundColor || 'var(--primary-2)';

  // If it's a CSS variable, use mono colors for text, otherwise calculate
  let textColor: string;
  if (backgroundColor.startsWith('var(')) {
    textColor = 'var(--mono-1)';
  } else {
    const calculatedTextColor = getTextColor(backgroundColor);
    // getTextColor returns '#fff' for light text or '' for dark text
    textColor = calculatedTextColor || '#000000';
  }

  let borderColor: string;
  if (backgroundColor.startsWith('var(')) {
    // Use a darker primary color for border when using CSS variables
    borderColor = 'var(--primary-1)';
  } else {
    // Calculate border color for custom colors
    // Use the text color to determine if background is light or dark
    const isLightBackground = textColor === '#000000';
    if (isLightBackground) {
      borderColor = darkenColor(backgroundColor, 0.3);
    } else {
      borderColor = backgroundColor === '#3174ad' ? 'var(--primary-1)' : backgroundColor;
    }
  }

  return { backgroundColor, borderColor, textColor };
};

/**
 * Convert record data to calendar event
 */
export const recordToCalendarEvent = (
  record: { flat: Record<string, unknown> },
  index: number,
  dateFieldInfo: {
    startFieldId: number | null;
    endFieldId: number | null;
    startFieldName?: string;
    endFieldName?: string;
  },
  workflowSteps?: Array<{ label: string; backgroundColor?: string }>,
  formName?: string
): CalendarEvent => {
  const item = record.flat;
  const colors = getWorkflowStepColors(item.status as string, workflowSteps);

  let startDate: string;
  let endDate: string | undefined;
  const allDay = true;

  if (dateFieldInfo.startFieldId) {
    // Get start date - MANDATORY
    startDate = getFieldValueById(item, dateFieldInfo.startFieldId) || new Date().toISOString();

    // Try to get end date - OPTIONAL
    if (dateFieldInfo.endFieldId) {
      const endDateValue = getFieldValueById(item, dateFieldInfo.endFieldId);
      if (endDateValue && endDateValue.trim() !== '') {
        endDate = endDateValue;

        // Parse dates to check if they're different days
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);

        // Validate that end date is not before start date
        if (endDateObj < startDateObj) {
          endDate = undefined; // Treat as single-day event
        } else if (startDateObj.toDateString() !== endDateObj.toDateString()) {
          // Different days - create a multi-day event
          // For FullCalendar, the end date should be the day AFTER the last day (exclusive)
          const adjustedEndDate = new Date(endDateObj);
          adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
          endDate = adjustedEndDate.toISOString().split('T')[0];
        } else {
          // Same day - single day event
          endDate = undefined;
        }
      }
    }
  } else {
    // Fallback to legacy fields
    startDate = (item.calendarViewDate as string) || new Date().toISOString();
    const fallbackEndDate = item.endDate as string | undefined;
    if (fallbackEndDate && fallbackEndDate.trim() !== '') {
      endDate = fallbackEndDate;

      // Apply same multi-day logic for fallback dates
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      // Validate that end date is not before start date
      if (endDateObj < startDateObj) {
        endDate = undefined; // Treat as single-day event
      } else if (startDateObj.toDateString() !== endDateObj.toDateString()) {
        const adjustedEndDate = new Date(endDateObj);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        endDate = adjustedEndDate.toISOString().split('T')[0];
      } else {
        // Same day - single day event
        endDate = undefined;
      }
    }
  }

  // Standard title format
  const docNumber = String(item.docNo || 'unknown').padStart(6, '0');
  const status = (item.status as string) || 'No Status';
  const displayFormName = formName || 'Record';
  const formattedTitle = `${displayFormName} - #${docNumber} - ${status}`;

  const calendarEvent = {
    id: (item.id as string) || index.toString(),
    title: formattedTitle,
    start: startDate,
    end: endDate, // undefined for single-day events, date string for multi-day events
    backgroundColor: colors.backgroundColor,
    borderColor: colors.borderColor,
    textColor: colors.textColor,
    allDay: allDay,
    extendedProps: {
      recordData: item,
      hasDateRange: !!endDate, // Track whether this event spans multiple days
    },
  };

  return calendarEvent;
};

/**
 * Creates a mouse down handler for popup dragging
 */
const mouseDownHandler =
  (
    popover: HTMLElement,
    dragState: { isDragging: boolean; startX: number; startY: number; startLeft: number; startTop: number },
    onMouseMove: (e: MouseEvent) => void,
    onMouseUp: () => void
  ) =>
  (e: MouseEvent) => {
    dragState.isDragging = true;
    dragState.startX = e.clientX;
    dragState.startY = e.clientY;
    const rect = popover.getBoundingClientRect();
    dragState.startLeft = rect.left;
    dragState.startTop = rect.top;

    popover.style.position = 'fixed';
    popover.style.left = dragState.startLeft + 'px';
    popover.style.top = dragState.startTop + 'px';
    popover.style.zIndex = '9999';

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    // Add cleanup for edge cases - user drags outside browser window
    document.addEventListener('visibilitychange', onMouseUp, { once: true });
    window.addEventListener('blur', onMouseUp, { once: true });
    e.preventDefault();
  };

/**
 * Creates a mouse move handler for popup dragging
 */
const mouseMoveHandler =
  (
    popover: HTMLElement,
    dragState: { isDragging: boolean; startX: number; startY: number; startLeft: number; startTop: number }
  ) =>
  (e: MouseEvent) => {
    if (!dragState.isDragging) return;
    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;
    popover.style.left = dragState.startLeft + deltaX + 'px';
    popover.style.top = dragState.startTop + deltaY + 'px';
  };

/**
 * Creates a mouse up handler for popup dragging
 */
const mouseUpHandler =
  (dragState: { isDragging: boolean }, onMouseMove: (e: MouseEvent) => void, onMouseUp: () => void) => () => {
    dragState.isDragging = false;
    cleanupEventListeners(onMouseMove, onMouseUp);
  };

/**
 * Styles the popup header for dragging
 */
const stylePopupHeader = (header: HTMLElement): void => {
  header.style.cursor = 'move';
  header.style.userSelect = 'none';
  header.style.backgroundColor = 'var(--gray-5)';
  header.style.borderBottom = '1px solid var(--gray-4)';
  header.style.padding = '8px 12px';
  header.style.fontWeight = 'bold';
};
/**
 * Remove mouse events
 */
const cleanupEventListeners = (onMouseMove: (e: MouseEvent) => void, onMouseUp: () => void) => {
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
  // Also remove the edge case listeners - user drags outside browser window
  document.removeEventListener('visibilitychange', onMouseUp);
  window.removeEventListener('blur', onMouseUp);
};

/**
 * Make FullCalendar popup draggable
 */
export const makePopupDraggable = (): void => {
  setTimeout(() => {
    const popover = document.querySelector('.fc-popover') as HTMLElement;
    if (popover && !popover.classList.contains('draggable-popup')) {
      popover.classList.add('draggable-popup');

      const header = popover.querySelector('.fc-popover-header') as HTMLElement;
      if (header) {
        stylePopupHeader(header);
      }

      const dragState = {
        isDragging: false,
        startX: 0,
        startY: 0,
        startLeft: 0,
        startTop: 0,
      };

      const handleMouseMove = mouseMoveHandler(popover, dragState);

      const handleMouseUp = mouseUpHandler(dragState, handleMouseMove, () => {
        dragState.isDragging = false;
        cleanupEventListeners(handleMouseMove, handleMouseUp);
      });

      const handleMouseDown = mouseDownHandler(popover, dragState, handleMouseMove, handleMouseUp);

      if (header) {
        header.addEventListener('mousedown', handleMouseDown);
      }
    }
  }, 50);
};

/**
 * Check if today is visible in the current calendar view
 * @param viewDate - The current view's reference date
 * @param viewType - The calendar view type
 * @returns true if today is visible in the current view
 */
export const isTodayVisible = (viewDate: Date, viewType: string): boolean => {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();

  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();

  switch (viewType) {
    case 'dayGridMonth':
      return todayYear === viewYear && todayMonth === viewMonth;

    case 'timeGridWeek': {
      const startOfWeek = new Date(viewDate);
      startOfWeek.setDate(viewDate.getDate() - viewDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return today >= startOfWeek && today <= endOfWeek;
    }

    case 'timeGridDay':
      return todayYear === viewYear && todayMonth === viewMonth && todayDay === viewDate.getDate();

    default:
      return true;
  }
};

/**
 * Calculate responsive calendar height based on screen size
 * Adjusts height for mobile, tablet, and desktop viewports
 *
 * @param defaultHeight - The default height for desktop (optional)
 * @returns CSS height string for the calendar
 */
export const getCalendarHeight = (defaultHeight: string = 'calc(100vh - 200px)'): string => {
  if (typeof window === 'undefined') return defaultHeight;

  const screenWidth = window.innerWidth;

  // Mobile: Give MORE height to show full day range
  if (screenWidth < 640) return 'calc(100vh - 200px)';

  // Tablet
  if (screenWidth < 1024) return 'calc(100vh - 120px)';

  // Desktop: Use provided default or standard height
  return defaultHeight;
};
